import { useMemo, useState } from 'react';
import type { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Bell,
  Boxes,
  BriefcaseBusiness,
  ClipboardList,
  Download,
  FileCog,
  FileSignature,
  FileText,
  FolderKanban,
  GitBranch,
  HardDrive,
  KeyRound,
  LayoutDashboard,
  type LucideIcon,
  Search,
  Truck,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { getApiErrorMessage } from '@/api/apiErrors';
import { getTenant } from '@/api/apiTenants';
import type { TenantUser } from '@/api/apiTypes';
import { AppDataGrid } from '@/components/grid/AppDataGrid';
import { useEnvironment } from '@/hooks/useEnvironments';
import { useTenantFeatureFlags, useUpdateTenantFeatureFlags } from '@/hooks/useFeatureFlags';
import { useTenantUsers } from '@/hooks/useTenantUsers';
import { useEnvironmentScope } from '@/providers/useEnvironmentScope';
import '@/pages/tenants/TenantDetailsPage.scss';
import { exportCsv } from '@/utils/csv';

const EMPTY_TENANT_USERS: TenantUser[] = [];
const INTEGER_FORMATTER = new Intl.NumberFormat('en-US');

type ModuleDefinition = {
  labelKey: string;
  icon: LucideIcon;
  featureName: string;
  /** Nested Operate scopes (e.g. Smart Alerts → Assets). */
  children?: ModuleDefinition[];
  /** When set, this child toggle is disabled unless the parent Manage module is on. */
  parentManageFeatureName?: string;
};

type ModuleSection = {
  titleKey: string;
  modules: ModuleDefinition[];
};

const MODULE_SECTIONS: ModuleSection[] = [
  {
    titleKey: 'tenantDetails.modules.sections.manage',
    modules: [
      { labelKey: 'tenantDetails.modules.items.assets', icon: Boxes, featureName: 'assets' },
      { labelKey: 'tenantDetails.modules.items.contracts', icon: FileText, featureName: 'contracts' },
      { labelKey: 'tenantDetails.modules.items.customers', icon: Users, featureName: 'customers' },
      { labelKey: 'tenantDetails.modules.items.licenses', icon: KeyRound, featureName: 'licenses' },
      { labelKey: 'tenantDetails.modules.items.projects', icon: FolderKanban, featureName: 'projects' },
      { labelKey: 'tenantDetails.modules.items.suppliers', icon: Truck, featureName: 'suppliers' },
      { labelKey: 'tenantDetails.modules.items.workOrders', icon: ClipboardList, featureName: 'work_orders' },
      { labelKey: 'tenantDetails.modules.items.workflow', icon: GitBranch, featureName: 'workflow' },
    ],
  },
  {
    titleKey: 'tenantDetails.modules.sections.documents',
    modules: [
      { labelKey: 'tenantDetails.modules.items.create', icon: FileCog, featureName: 'doc_create' },
      { labelKey: 'tenantDetails.modules.items.sign', icon: FileSignature, featureName: 'doc_sign' },
      { labelKey: 'tenantDetails.modules.items.smartRepository', icon: BriefcaseBusiness, featureName: 'smart_repository' },
    ],
  },
  {
    titleKey: 'tenantDetails.modules.sections.reportAndAnalyze',
    modules: [
      { labelKey: 'tenantDetails.modules.items.customDashboard', icon: LayoutDashboard, featureName: 'custom_dashboard' },
    ],
  },
  {
    // Operate is the home for cross-cutting operational modules (Smart Alerts now,
    // Maintenance Tasks and similar later) rather than primary business objects.
    titleKey: 'tenantDetails.modules.sections.operate',
    modules: [
      {
        labelKey: 'tenantDetails.modules.items.smartAlerts',
        icon: Bell,
        featureName: 'smart_alerts',
        children: [
          {
            labelKey: 'tenantDetails.modules.items.smartAlertsAssets',
            icon: Boxes,
            featureName: 'smart_alerts_assets',
            parentManageFeatureName: 'assets',
          },
          {
            labelKey: 'tenantDetails.modules.items.smartAlertsContracts',
            icon: FileText,
            featureName: 'smart_alerts_contracts',
            parentManageFeatureName: 'contracts',
          },
          {
            labelKey: 'tenantDetails.modules.items.smartAlertsLicenses',
            icon: KeyRound,
            featureName: 'smart_alerts_licenses',
            parentManageFeatureName: 'licenses',
          },
        ],
      },
    ],
  },
];

function formatInteger(value: number) {
  return INTEGER_FORMATTER.format(value);
}

function formatBytes(bytes: number) {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unitIndex = -1;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatName(user: TenantUser) {
  const fullName = `${user.first_name} ${user.last_name}`.trim();
  return fullName || user.email;
}

function formatUserId(value: string) {
  return value || '-';
}

function initialsFromName(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function TenantDetailsPage() {
  const { t } = useTranslation();
  const { tenantId } = useParams();
  const parsedTenantId = Number(tenantId);
  const [searchTerm, setSearchTerm] = useState('');
  const [usersPageSize, setUsersPageSize] = useState<number>(10);
  const { selectedEnvironmentId, setEnvironmentScope } = useEnvironmentScope();
  const selectedEnvironmentQuery = useEnvironment(selectedEnvironmentId);

  const tenantQuery = useQuery({
    queryKey: ['tenant', 'detail', parsedTenantId],
    queryFn: () => getTenant(parsedTenantId),
    enabled: Number.isFinite(parsedTenantId),
  });
  const usersQuery = useTenantUsers(
    Number.isFinite(parsedTenantId) ? parsedTenantId : undefined,
    usersPageSize,
  );
  const tenantUsers = usersQuery.data ?? EMPTY_TENANT_USERS;

  // Feature flags
  const featureFlagsQuery = useTenantFeatureFlags(Number.isFinite(parsedTenantId) ? parsedTenantId : undefined);
  const updateFeatureFlags = useUpdateTenantFeatureFlags(Number.isFinite(parsedTenantId) ? parsedTenantId : undefined);

  const searchableUsers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return tenantUsers;
    }

    return tenantUsers.filter((user) => {
      const haystack = `${user.first_name} ${user.last_name} ${user.email}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [searchTerm, tenantUsers]);

  const userColumnDefs = useMemo<ColDef<TenantUser>[]>(
    () => [
      {
        headerName: t('tenantDetails.users.columns.name'),
        valueGetter: ({ data }) => (data ? formatName(data) : ''),
        flex: 2,
        minWidth: 240,
        cellRenderer: ({ data }: { data?: TenantUser }) => {
          if (!data) {
            return null;
          }

          const name = formatName(data);

          return (
            <div className="user-identity-cell">
              <span className="user-identity-cell__avatar">{initialsFromName(name)}</span>
              <span className="user-identity-cell__content">
                <span className="user-identity-cell__name">{name}</span>
                <span className="user-identity-cell__meta">{data.email}</span>
              </span>
            </div>
          );
        },
      },
      {
        headerName: t('tenantDetails.users.columns.email'),
        field: 'email',
        flex: 2,
        minWidth: 240,
      },
      {
        headerName: t('tenantDetails.users.columns.userId'),
        field: 'user_id',
        flex: 1,
        minWidth: 190,
        valueFormatter: ({ value }: ValueFormatterParams<TenantUser>) => formatUserId(String(value ?? '')),
      },
    ],
    [t],
  );

  const stats = tenantQuery.data?.statistics;
  const metricCards = [
    { label: t('tenantDetails.metrics.assets'), value: formatInteger(stats?.total_assets ?? 0), icon: Boxes, tone: 'green' },
    { label: t('tenantDetails.metrics.contracts'), value: formatInteger(stats?.total_contracts ?? 0), icon: FileText, tone: 'violet' },
    { label: t('tenantDetails.metrics.suppliers'), value: formatInteger(stats?.total_suppliers ?? 0), icon: Truck, tone: 'amber' },
    { label: t('tenantDetails.metrics.customers'), value: formatInteger(stats?.total_customers ?? 0), icon: Users, tone: 'rose' },
    { label: t('tenantDetails.metrics.licenses'), value: formatInteger(stats?.total_licenses ?? 0), icon: KeyRound, tone: 'blue' },
    { label: t('tenantDetails.metrics.workOrders'), value: formatInteger(stats?.total_workorders ?? 0), icon: ClipboardList, tone: 'amber' },
    { label: t('tenantDetails.metrics.storage'), value: formatBytes(stats?.storage_bytes ?? 0), icon: HardDrive, tone: 'green' },
    { label: t('tenantDetails.metrics.users'), value: formatInteger(stats?.total_users ?? tenantUsers.length), icon: Users, tone: 'slate' },
  ];

  const handleExportUsers = () => {
    if (!tenantQuery.data) {
      return;
    }

    exportCsv(`tenant-users-${tenantQuery.data.schema_name}.csv`, [
      { key: 'environmentName', label: t('tenantDetails.export.environmentName'), resolve: () => tenantQuery.data!.environment.name },
      { key: 'environmentId', label: t('tenantDetails.export.environmentId'), resolve: () => tenantQuery.data!.environment.id },
      { key: 'tenantId', label: t('tenantDetails.export.tenantId'), resolve: () => tenantQuery.data!.id },
      { key: 'timestamp', label: t('tenantDetails.export.timestamp'), resolve: () => new Date().toISOString() },
      { key: 'name', label: t('tenantDetails.users.columns.name'), resolve: (user) => formatName(user) },
      { key: 'email', label: t('tenantDetails.users.columns.email'), resolve: (user) => user.email },
      { key: 'userId', label: t('tenantDetails.users.columns.userId'), resolve: (user) => user.user_id },
    ], searchableUsers);
  };

  const handleToggleFeature = (featureName: string, currentValue: boolean) => {
    if (!updateFeatureFlags || !parsedTenantId) {
      return;
    }

    updateFeatureFlags.mutate({
      features: [
        {
          name: featureName,
          enabled: !currentValue,
        },
      ],
    });
  };

  if (!Number.isFinite(parsedTenantId)) {
    return (
      <div className="empty-state">
        <h1>{t('tenantDetails.invalidTitle')}</h1>
        <p>{t('tenantDetails.invalidDescription')}</p>
      </div>
    );
  }

  if (tenantQuery.isPending) {
    return <div className="empty-state"><p>{t('tenantDetails.messages.loadingTenant')}</p></div>;
  }

  if (tenantQuery.isError || !tenantQuery.data) {
    return (
      <div className="empty-state">
        <h1>{t('tenantDetails.messages.loadTenantErrorTitle')}</h1>
        <p>{getApiErrorMessage(tenantQuery.error, t('tenantDetails.messages.loadTenantErrorBody'))}</p>
      </div>
    );
  }

  const isScopeMismatch =
    selectedEnvironmentId !== undefined && tenantQuery.data.environment.id !== selectedEnvironmentId;

  const selectedScopeName = selectedEnvironmentQuery.data?.name ?? t('dashboard.scope.unknown');

  return (
    <div className="page-stack tenant-details-page">
      <Link className="tenant-back-link" to="/">
        <ArrowLeft size={16} />
        {t('tenantDetails.backToDashboard')}
      </Link>

      {isScopeMismatch ? (
        <article className="surface-card surface-card--soft tenant-scope-notice">
          <div>
            <h2>{t('tenantDetails.scopeMismatch.title')}</h2>
            <p>
              {t('tenantDetails.scopeMismatch.description', {
                environment: tenantQuery.data.environment.name,
              })}
            </p>
            <p>
              {t('tenantDetails.scopeMismatch.currentScope', {
                environment: selectedScopeName,
              })}
            </p>
          </div>
          <div className="tenant-scope-notice__actions">
            <button
              type="button"
              className="button button--primary"
              onClick={() => setEnvironmentScope(String(tenantQuery.data.environment.id) as `${number}`)}
            >
              {t('tenantDetails.scopeMismatch.switchToTenantEnvironment')}
            </button>
            <button
              type="button"
              className="button button--secondary"
              onClick={() => setEnvironmentScope('all')}
            >
              {t('tenantDetails.scopeMismatch.viewAllAssigned')}
            </button>
          </div>
        </article>
      ) : null}

      <article className="surface-card tenant-profile-card">
        <div className="tenant-profile-card__identity">
          <span className="tenant-profile-card__avatar" aria-hidden="true">
            {initialsFromName(tenantQuery.data.name)}
          </span>
          <div className="tenant-profile-card__copy">
            <div className="tenant-profile-card__title-row">
              <h1>{tenantQuery.data.name}</h1>
              <span className={`status-pill ${tenantQuery.data.is_active ? 'status-pill--active' : 'status-pill--inactive'}`}>
                {tenantQuery.data.is_active ? t('dashboard.status.active') : t('dashboard.status.inactive')}
              </span>
              <span className="environment-pill">{tenantQuery.data.environment.name}</span>
            </div>
            <p>{t('tenantDetails.heroDescription', { tenantId: tenantQuery.data.id, subscriptionId: tenantQuery.data.schema_name })}</p>
          </div>
        </div>

        <div className="tenant-profile-card__facts">
          <div className="tenant-profile-card__fact">
            <span>{t('tenantDetails.profile.tenantId')}</span>
            <strong>{tenantQuery.data.id}</strong>
          </div>
          <div className="tenant-profile-card__fact">
            <span>{t('tenantDetails.profile.subscriptionId')}</span>
            <strong>{tenantQuery.data.schema_name}</strong>
          </div>
        </div>
      </article>

      <section className="dashboard-summary-grid tenant-metrics-grid">
        {metricCards.map((metric) => {
          const Icon = metric.icon;

          return (
            <article key={metric.label} className="surface-card summary-card summary-card--compact">
              <div className="summary-card__head">
                <div>
                  <p className="metric-label">{metric.label}</p>
                  <h2 className="metric-value">{metric.value}</h2>
                </div>
                <span className={`summary-card__icon summary-card__icon--${metric.tone}`}>
                  <Icon size={20} />
                </span>
              </div>
            </article>
          );
        })}
      </section>

      <article className="surface-card tenant-modules-card">
        <div className="dashboard-overview-card__header">
          <div className="section-heading">
            <span className="section-heading__icon">
              <FileCog size={18} />
            </span>
            <div className="section-heading__copy">
              <p className="section-heading__title">{t('tenantDetails.modulesTitle')}</p>
              <p className="section-copy">
                {featureFlagsQuery.isPending
                  ? t('tenantDetails.modulesLoading')
                  : featureFlagsQuery.isError
                    ? t('tenantDetails.modulesError')
                    : t('tenantDetails.modulesDescription')}
              </p>
            </div>
          </div>
        </div>

        <div className="tenant-modules-card__sections">
          {MODULE_SECTIONS.map((section) => (
            <section key={section.titleKey} className="tenant-modules-section">
              <p className="tenant-modules-section__title">{t(section.titleKey)}</p>
              <div className="tenant-modules-section__grid">
                {section.modules.map((module) => {
                  const Icon = module.icon;
                  const feature = featureFlagsQuery.data?.features?.find((f) => f.name === module.featureName);
                  const isEnabled = feature?.enabled ?? false;
                  const isLoading = featureFlagsQuery.isPending || updateFeatureFlags.isPending;
                  const hasError = featureFlagsQuery.isError;
                  const isDisabled = isLoading || hasError || !featureFlagsQuery.data;

                  return (
                    <div key={module.labelKey} className="tenant-module-card-stack">
                      <button
                        type="button"
                        className={`tenant-module-card ${isEnabled ? 'tenant-module-card--active' : ''}`}
                        disabled={isDisabled}
                        onClick={() => handleToggleFeature(module.featureName, isEnabled)}
                        aria-label={t(module.labelKey)}
                        aria-pressed={isEnabled}
                      >
                        <span className="tenant-module-card__meta">
                          <span className="tenant-module-card__icon" aria-hidden="true">
                            <Icon size={16} />
                          </span>
                          <span className="tenant-module-card__label">{t(module.labelKey)}</span>
                        </span>
                        <span className={`tenant-module-card__toggle ${isEnabled ? 'tenant-module-card__toggle--active' : ''}`} aria-hidden="true">
                          <span className="tenant-module-card__toggle-knob" />
                        </span>
                      </button>

                      {module.children?.length ? (
                        <div className="tenant-module-card-stack__children" role="group" aria-label={t(module.labelKey)}>
                          {module.children.map((child) => {
                            const ChildIcon = child.icon;
                            const childFeature = featureFlagsQuery.data?.features?.find(
                              (f) => f.name === child.featureName,
                            );
                            const isChildEnabled = childFeature?.enabled ?? false;
                            const parentManageFeature = child.parentManageFeatureName
                              ? featureFlagsQuery.data?.features?.find(
                                  (f) => f.name === child.parentManageFeatureName,
                                )
                              : undefined;
                            const isParentManageEnabled = child.parentManageFeatureName
                              ? Boolean(parentManageFeature?.enabled)
                              : true;
                            // Master hard-gate: children stay visible but cannot change while master is off.
                            // Parent Manage module must also be on for that child scope to be editable.
                            const isChildDisabled =
                              isDisabled || !isEnabled || !isParentManageEnabled;

                            return (
                              <button
                                key={child.labelKey}
                                type="button"
                                className={`tenant-module-card tenant-module-card--child ${isChildEnabled ? 'tenant-module-card--active' : ''}`}
                                disabled={isChildDisabled}
                                onClick={() => handleToggleFeature(child.featureName, isChildEnabled)}
                                aria-label={t(child.labelKey)}
                                aria-pressed={isChildEnabled}
                              >
                                <span className="tenant-module-card__meta">
                                  <span className="tenant-module-card__icon" aria-hidden="true">
                                    <ChildIcon size={16} />
                                  </span>
                                  <span className="tenant-module-card__label">{t(child.labelKey)}</span>
                                </span>
                                <span
                                  className={`tenant-module-card__toggle ${isChildEnabled ? 'tenant-module-card__toggle--active' : ''}`}
                                  aria-hidden="true"
                                >
                                  <span className="tenant-module-card__toggle-knob" />
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </article>

      <article className="surface-card tenant-users-card">
        <div className="dashboard-overview-card__header">
          <div className="section-heading">
            <span className="section-heading__icon">
              <Users size={18} />
            </span>
            <div className="section-heading__copy">
              <p className="section-heading__title">{t('tenantDetails.users.title')}</p>
              <p className="section-copy">{t('tenantDetails.users.description', { count: tenantUsers.length })}</p>
            </div>
          </div>
          <button type="button" className="button button--secondary tenant-users-card__export-btn" onClick={handleExportUsers}>
            <Download size={16} />
            {t('tenantDetails.users.export')}
          </button>
        </div>

        <div className="dashboard-overview-card__filters">
          <label className="dashboard-search-field">
            <Search size={16} />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t('tenantDetails.users.searchPlaceholder')}
            />
          </label>
        </div>

        {usersQuery.isPending ? <p>{t('tenantDetails.users.loading')}</p> : null}
        {usersQuery.isError ? <p>{getApiErrorMessage(usersQuery.error, t('tenantDetails.users.loadError'))}</p> : null}

        {searchableUsers.length ? (
          <AppDataGrid
            rowData={searchableUsers}
            columnDefs={userColumnDefs}
            quickFilterText={searchTerm}
            pageSize={usersPageSize}
            onPageSizeChange={setUsersPageSize}
            height={520}
            className="app-data-grid--tenant-users"
          />
        ) : null}

        {!usersQuery.isPending && searchableUsers.length === 0 ? <p>{t('tenantDetails.users.empty')}</p> : null}
      </article>
    </div>
  );
}